import React, { useState, useEffect } from 'react';
import { Box, Paper } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchBalanceSheet, downloadBalanceSheet } from '../../store/reportStore/reportThunk';
import BalanceSheet from '../../components/Reports/BalanceSheet';
import ReusableReportHeader from '../../components/Reports/ReusableReportHeader';
import { useReportShare } from '../../components/Reports/useReportShare';
import Filters from '../../components/Filters/Filters';
import { ApiStatus } from '../../utils/api.types';
import { VocuherCategory } from '../../utils/common-utils';
import { toast } from 'react-toastify';

const BalanceSheetPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { balanceSheetData, status, errorMessage } = useAppSelector((state) => state.reports);
  const { selectedAccount } = useAppSelector((state) => state.auth);
  const accountId = selectedAccount?._id;

  const [filters, setFilters] = useState('');
  const [filterData, setFilterData] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Share-by-email (account email + configured recipients). getParams reads the current filters.
  const { handleShare, shareTooltip } = useReportShare('balance-sheet', () => filters);

  const filterOnChangeHandler = (data: any) => {
    setFilters(data);
  };

  const filterdOnChangeHandler = (data: any) => {
    setFilterData(data);
  };

  // Fetch report when filters change
  useEffect(() => {
    if (accountId && filters) {
      setIsLoading(true);
      dispatch(fetchBalanceSheet({
        accountId,
        startDate: '',
        endDate: '',
        params: filters
      })).finally(() => {
        setIsLoading(false);
      });
    }
  }, [accountId, filters]);

  const handleDownloadPdf = () => {
    if (!accountId || !filters) {
      toast.error("Please select date range to download PDF");
      return false;
    }

    return dispatch(downloadBalanceSheet({
      accountId,
      startDate: '',
      endDate: '',
      params: `${filters}&isDownloadPdf=true`
    })).unwrap().then((data) => {
      if (data) {
        // Create a Blob from the response data
        const blob = new Blob([data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        // Create a link element, set the href, and trigger the download
        const a = document.createElement("a");
        a.href = url;
        const timestamp = new Date().toISOString().split('T')[0];
        a.download = `Balance_Sheet_${timestamp}.pdf`;
        document.body.appendChild(a);
        a.click();

        // Cleanup
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    });
  };

  const handleDownloadExcel = () => {
    if (!accountId || !filters) {
      toast.error("Please select date range to download Excel");
      return false;
    }

    return dispatch(downloadBalanceSheet({
      accountId,
      startDate: '',
      endDate: '',
      params: `${filters}&isDownloadExcel=true`
    })).unwrap().then((data) => {
      if (data) {
        // Create a Blob from the response data
        const blob = new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        const url = window.URL.createObjectURL(blob);

        // Create a link element, set the href, and trigger the download
        const a = document.createElement("a");
        a.href = url;
        const timestamp = new Date().toISOString().split('T')[0];
        a.download = `Balance_Sheet_${timestamp}.xlsx`;
        document.body.appendChild(a);
        a.click();

        // Cleanup
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    });
  };

  return (
    <Box sx={{ width: 'calc(100% - 7px)', height: 'calc(100vh - 70px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', mb: 2 }} className={'c-card2'}>
      {/* Scrollable content area */}
      <Box sx={{
        flex: 1,
        overflow: 'auto',
        minHeight: 0,
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none'
      }}>
        {/* Report Header */}
        <ReusableReportHeader
          reportType="BALANCE_SHEET"
          title="Balance Sheet"
          subtitle={filterData?.periodString || ''}
          isLoading={isLoading || status === ApiStatus.LOADING}
          onDownloadPDF={handleDownloadPdf}
          onDownloadExcel={handleDownloadExcel}
          onShare={handleShare}
          shareTooltip={shareTooltip}
          showPrint={false}
        />

        {/* Filters - No Paper wrapper */}
        <Box px={2} py={1.5}>
          <Filters
            type={VocuherCategory.SALE}
            filterHandler={filterOnChangeHandler}
            selectedFilters={filterdOnChangeHandler}
            hideFilter={true}
            hidePartySearch={true}
          />
        </Box>

        {/* Balance Sheet */}
        <Box px={2}>
          <BalanceSheet
            data={balanceSheetData}
            loading={status === ApiStatus.LOADING || isLoading}
            error={errorMessage}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default BalanceSheetPage;
